/**
 * LinkedConfluencesDualList Component
 *
 * Displays linked confluences in a dual-list layout separated by polarity.
 * - Desktop (lg+): Side-by-side columns with move arrows in center
 * - Mobile/Tablet: Stacked sections (Positive on top, Negative below) with move bars
 *
 * Each row shows:
 * - Confluence name
 * - Category badge (color swatch + name, or "No category" text)
 * - Archived indicator if confluence is archived
 * - Unlink button (hidden when onUnlink is undefined)
 *
 * Selection behavior:
 * - Selecting in one list clears selection in the other (prevents ambiguous moves)
 * - Entire row is clickable to toggle selection
 * - Move operation changes polarity and clears selection
 *
 * Sorted: active items first, then alphabetically by name within each polarity list.
 *
 * Data is loaded once on mount and refreshed via cross-tab sync subscriptions
 * for confluences and market_condition_categories tables.
 */

import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Plus,
  X,
  Pencil,
  ThumbsUp,
  ThumbsDown,
  ArrowRight,
  ArrowLeft,
  ArrowDown,
  ArrowUp,
} from 'lucide-react';

import {
  Button,
  Card,
  CardHeader,
  CardBody,
  Chip,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
} from '@enfomentis/ui';
import { confluenceService } from '@/services/ConfluenceService';
import { marketConditionCategoryService } from '@/services/MarketConditionCategoryService';
import { syncCoordinatorService } from '@/services/sync/SyncCoordinatorService';
import type { Confluence } from '@/types/schema/Confluence';
import type { MarketConditionCategory } from '@/types/schema/MarketConditionCategory';
import type { PlaybookConfluenceLink } from '@/types/schema/Playbook';

export interface LinkedConfluencesDualListProps {
  /** Array of confluence links (with polarity) linked to the playbook */
  confluences: PlaybookConfluenceLink[];
  /** Handler for unlink action - hidden when undefined */
  onUnlink?: (confluenceId: string) => void;
  /** Handler for edit action - opens edit modal with confluence data */
  onEdit?: (confluence: Confluence) => void;
  /** Handler for add existing action - opens picker modal */
  onAdd?: () => void;
  /** Handler for create new action - opens create modal */
  onCreateNew?: () => void;
  /** Set of selected positive confluence IDs */
  positiveSelected?: Set<string>;
  /** Set of selected negative confluence IDs */
  negativeSelected?: Set<string>;
  /** Handler for selection change in a polarity list */
  onSelectionChange?: (polarity: 'positive' | 'negative', selected: Set<string>) => void;
  /** Handler for move operation */
  onMove?: (direction: 'toPositive' | 'toNegative') => void;
}

/** Sort by status (active first) then alphabetically by name (case-insensitive) */
const sortByStatusThenName = <T extends { status: string; name: string }>(
  items: T[]
): T[] => {
  return [...items].sort((a, b) => {
    // Active items first
    if (a.status === 'active' && b.status !== 'active') return -1;
    if (a.status !== 'active' && b.status === 'active') return 1;
    // Then alphabetically by name (case-insensitive)
    return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
  });
};

/** Enriched confluence with polarity for display */
interface EnrichedConfluence extends Confluence {
  polarity: 'positive' | 'negative';
}

export const LinkedConfluencesDualList: React.FC<LinkedConfluencesDualListProps> = ({
  confluences,
  onUnlink,
  onEdit,
  onAdd,
  onCreateNew,
  positiveSelected = new Set(),
  negativeSelected = new Set(),
  onSelectionChange,
  onMove,
}) => {
  // Local state for cached confluences and categories
  const [allConfluences, setAllConfluences] = useState<Confluence[]>([]);
  const [categories, setCategories] = useState<MarketConditionCategory[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Shared popover state - single source so only one menu can be open
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  /**
   * Render the Add Confluences popover with "Create New..." and "Add Existing..." options.
   * Used in both empty state and list bottom to maintain consistency.
   */
  const renderAddPopover = (variant: 'flat' | 'light' = 'light') => {
    // Only show if at least one handler is defined
    if (!onAdd && !onCreateNew) return null;

    return (
      <Popover
        isOpen={isPopoverOpen}
        onOpenChange={setIsPopoverOpen}
        placement="bottom-start"
      >
        <PopoverTrigger>
          <Button
            size="sm"
            variant={variant}
            color={variant === 'flat' ? 'primary' : 'default'}
            startContent={<Plus size={16} />}
            aria-label="Add Confluences"
          >
            Add Confluences
          </Button>
        </PopoverTrigger>
        <PopoverContent className="p-1">
          <div className="flex flex-col min-w-40">
            {onCreateNew && (
              <Button
                size="md"
                variant="light"
                className="justify-start"
                onPress={() => {
                  setIsPopoverOpen(false);
                  onCreateNew();
                }}
              >
                Create New...
              </Button>
            )}
            {onAdd && (
              <Button
                size="md"
                variant="light"
                className="justify-start"
                onPress={() => {
                  setIsPopoverOpen(false);
                  onAdd();
                }}
              >
                Add Existing...
              </Button>
            )}
          </div>
        </PopoverContent>
      </Popover>
    );
  };

  // Load all confluences and categories data
  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [confluenceData, categoryData] = await Promise.all([
        confluenceService.getAll(),
        marketConditionCategoryService.getAll(),
      ]);
      setAllConfluences(confluenceData);
      setCategories(categoryData);
    } catch (error) {
      console.error('Failed to load confluence data:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load data on mount
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Subscribe to cross-tab sync for confluences changes
  useEffect(() => {
    const unsubscribe = syncCoordinatorService.subscribe('confluences', () => {
      loadData();
    });
    return () => unsubscribe();
  }, [loadData]);

  // Subscribe to cross-tab sync for category changes
  useEffect(() => {
    const unsubscribe = syncCoordinatorService.subscribe('market_condition_categories', () => {
      loadData();
    });
    return () => unsubscribe();
  }, [loadData]);

  // Build category lookup map
  const categoryMap = useMemo(() => {
    const map = new Map<string, MarketConditionCategory>();
    categories.forEach((cat) => map.set(cat.id, cat));
    return map;
  }, [categories]);

  // Build confluence lookup map
  const confluenceMap = useMemo(() => {
    const map = new Map<string, Confluence>();
    allConfluences.forEach((c) => map.set(c.id, c));
    return map;
  }, [allConfluences]);

  // Filter and sort linked confluences by polarity
  const { positiveConfluences, negativeConfluences } = useMemo(() => {
    const positive: EnrichedConfluence[] = [];
    const negative: EnrichedConfluence[] = [];

    confluences.forEach((link) => {
      const confluence = confluenceMap.get(link.confluenceId);
      if (confluence) {
        const enriched: EnrichedConfluence = { ...confluence, polarity: link.polarity };
        if (link.polarity === 'positive') {
          positive.push(enriched);
        } else {
          negative.push(enriched);
        }
      }
    });

    return {
      positiveConfluences: sortByStatusThenName(positive),
      negativeConfluences: sortByStatusThenName(negative),
    };
  }, [confluences, confluenceMap]);

  const totalCount = positiveConfluences.length + negativeConfluences.length;

  /**
   * Handle row click to toggle selection
   */
  const handleRowClick = (
    polarity: 'positive' | 'negative',
    confluenceId: string,
    currentSelected: Set<string>
  ) => {
    if (!onSelectionChange) return;

    const newSelected = new Set(currentSelected);
    if (newSelected.has(confluenceId)) {
      newSelected.delete(confluenceId);
    } else {
      newSelected.add(confluenceId);
    }
    onSelectionChange(polarity, newSelected);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8 text-text-tertiary text-sm">
        Loading...
      </div>
    );
  }

  // Empty state - both lists empty
  if (totalCount === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 gap-4 text-center">
        <div className="flex flex-col gap-1">
          <span className="text-text-primary text-sm font-medium">No confluences linked yet</span>
          <span className="text-text-secondary text-xs max-w-sm">
            Positive confluences increase your confidence; negative ones signal caution.
            Add confluences to track the factors that influence your trading decisions.
          </span>
        </div>
        {renderAddPopover('flat')}
      </div>
    );
  }

  // Render a single confluence item
  const renderConfluenceItem = (
    confluence: EnrichedConfluence,
    selected: Set<string>
  ) => {
    const category = confluence.marketConditionCategoryId
      ? categoryMap.get(confluence.marketConditionCategoryId)
      : undefined;
    const isArchived = confluence.status === 'archived';
    const isSelected = selected.has(confluence.id);
    const hasSelection = onSelectionChange !== undefined;

    // Determine background styling with priority: selected > archived > default
    const cardBgClass = isSelected ? 'border-2 border-primary/80 bg-default-100 dark:bg-default-200/40' : '';
    const rowBgClass = !isSelected && isArchived ? 'bg-default-50' : '';

    // Keyboard handler for accessibility (Enter/Space to toggle selection)
    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault(); // Prevent page scroll on Space
        handleRowClick(confluence.polarity, confluence.id, selected);
      }
    };

    return (
      <Card
        key={confluence.id}
        radius="sm"
        shadow="none"
        className={`border border-default-200 overflow-hidden w-full ${cardBgClass}`}
      >
        <CardBody className="p-0">
          <div
            role={hasSelection ? 'button' : undefined}
            tabIndex={hasSelection ? 0 : undefined}
            onClick={hasSelection ? () => handleRowClick(confluence.polarity, confluence.id, selected) : undefined}
            onKeyDown={hasSelection ? handleKeyDown : undefined}
            className={`flex items-center gap-3 p-2 min-w-0 overflow-hidden ${
              isArchived ? 'opacity-60' : ''
            } ${rowBgClass} ${hasSelection ? 'cursor-pointer' : ''}`}
          >
            {/* Name + Description + Archived Badge */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center min-w-0">
                <span
                  className={`font-medium truncate text-sm ${
                    isArchived ? 'text-text-tertiary' : 'text-text-primary'
                  }`}
                >
                  {confluence.name}
                </span>
                {isArchived && (
                  <Chip className="ml-2 shrink-0 text-xs text-text-tertiary bg-default-100">
                    Archived
                  </Chip>
                )}
              </div>
              {confluence.description && (
                <span className="text-sm text-text-secondary truncate block">
                  {confluence.description}
                </span>
              )}
            </div>

            {/* Category Badge */}
            <div className="shrink-0">
              {category ? (
                <Chip
                  size="sm"
                  variant="flat"
                  className="text-xs"
                  style={{ backgroundColor: `${category.color}20`, color: category.color }}
                  startContent={
                    <span
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: category.color }}
                    />
                  }
                >
                  {category.name}
                </Chip>
              ) : (
                <span className="text-xs text-text-tertiary">No category</span>
              )}
            </div>

            {/* Action Buttons (edit + unlink) */}
            {(onEdit || onUnlink) && (
              <div className="shrink-0 flex gap-1" onClick={(e) => e.stopPropagation()}>
                {onEdit && (
                  <Tooltip content="Edit Confluence">
                    <Button
                      size="sm"
                      variant="light"
                      isIconOnly
                      aria-label={`Edit ${confluence.name}`}
                      onPress={() => onEdit(confluence)}
                    >
                      <Pencil size={16} />
                    </Button>
                  </Tooltip>
                )}
                {onUnlink && (
                  <Tooltip content="Unlink Confluence">
                    <Button
                      size="sm"
                      variant="light"
                      isIconOnly
                      aria-label={`Unlink ${confluence.name}`}
                      onPress={() => onUnlink(confluence.id)}
                    >
                      <X size={16} />
                    </Button>
                  </Tooltip>
                )}
              </div>
            )}
          </div>
        </CardBody>
      </Card>
    );
  };

  // Render move bar for mobile (between stacked sections)
  const renderMoveBar = (
    direction: 'toPositive' | 'toNegative',
    hasSelection: boolean
  ) => {
    if (!onMove || !hasSelection) return null;

    const isToNegative = direction === 'toNegative';
    const Icon = isToNegative ? ArrowDown : ArrowUp;
    const label = isToNegative ? 'Move to Negative' : 'Move to Positive';

    return (
      <div className="lg:hidden py-2">
        <Button
          size="sm"
          variant="flat"
          color={isToNegative ? "warning" : "success"}
          className="w-full"
          startContent={<Icon size={16} />}
          onPress={() => onMove(direction)}
        >
          {label}
        </Button>
      </div>
    );
  };

  // Render a polarity section (column on desktop, stacked section on mobile)
  const renderPolaritySection = (
    polarity: 'positive' | 'negative',
    items: EnrichedConfluence[],
    selected: Set<string>
  ) => {
    const isPositive = polarity === 'positive';
    const Icon = isPositive ? ThumbsUp : ThumbsDown;
    const iconColor = isPositive ? 'text-success' : 'text-warning';
    const label = isPositive ? 'Positive' : 'Negative';

    return (
      <Card shadow="sm" radius="none" className="min-w-0 h-full border-1 border-default-200">
        {/* Header */}
        <CardHeader className="flex items-center gap-2 bg-default-50">
          <Icon size={16} className={iconColor} />
          <span className="text-sm font-medium text-text-primary">
            {label} ({items.length})
          </span>
        </CardHeader>
        <CardBody >
          {/* Items or empty state */}
          {items.length > 0 ? (
            <div className="flex flex-col gap-2">
              {items.map((item) => renderConfluenceItem(item, selected))}
            </div>
          ) : (
            <div className="py-2 text-center text-text-tertiary text-xs">
              None
            </div>
          )}
        </CardBody>
      </Card>
    );
  };

  // Render desktop move arrows column (center between lists)
  const renderMoveArrows = () => {
    if (!onMove) return null;

    const hasPositiveSelection = positiveSelected.size > 0;
    const hasNegativeSelection = negativeSelected.size > 0;

    return (
      <div className="hidden lg:flex flex-col items-center justify-center gap-2 min-w-12">
        <Button
          size="sm"
          variant="flat"
          color="primary"
          isIconOnly
          isDisabled={!hasPositiveSelection}
          aria-label="Move selected to Negative"
          onPress={() => onMove('toNegative')}
        >
          <ArrowRight size={16} />
        </Button>
        <Button
          size="sm"
          variant="flat"
          color="primary"
          isIconOnly
          isDisabled={!hasNegativeSelection}
          aria-label="Move selected to Positive"
          onPress={() => onMove('toPositive')}
        >
          <ArrowLeft size={16} />
        </Button>
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Subtitle */}
      <p
        className="text-xs text-text-secondary truncate"
        title="Factors that increase or decrease your confidence in a trade"
      >
        Factors that increase or decrease your confidence in a trade
      </p>

      {/* Dual-list layout: 3-column grid on desktop (list, arrows, list), stacked on mobile/tablet */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr] gap-4">
        {/* Positive section */}
        <div className="flex flex-col gap-2 min-w-0 overflow-hidden">
          {renderPolaritySection('positive', positiveConfluences, positiveSelected)}
          {/* Mobile move bar - below positive when positive items selected */}
          {renderMoveBar('toNegative', positiveSelected.size > 0)}
        </div>

        {/* Desktop move arrows (center column) */}
        {renderMoveArrows()}

        {/* Negative section */}
        <div  className="flex flex-col gap-2 min-w-0 overflow-hidden">
          {/* Mobile move bar - below negative when negative items selected */}
          {renderMoveBar('toPositive', negativeSelected.size > 0)}
          {renderPolaritySection('negative', negativeConfluences, negativeSelected)}
        </div>
      </div>

      {/* Add button at bottom */}
      <div className="self-center">{renderAddPopover('light')}</div>
    </div>
  );
};
